import React, { useEffect, useMemo, useState } from 'react';

function readQuery() {
  const params = new URLSearchParams(window.location.search);
  return {
    token: params.get('token') || '',
    website: params.get('website') || '',
  };
}

export default function UnsubPage() {
  const query = useMemo(() => readQuery(), []);
  const [status, setStatus] = useState(query.token ? 'loading' : 'idle');
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [emailType, setEmailType] = useState('');
  const [categories, setCategories] = useState([]);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!query.token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`/api/email-unsubscribe?token=${encodeURIComponent(query.token)}`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Could not load email preferences.');
        if (cancelled) return;
        setCompanyName(data.companyName || '');
        setSourceLabel(data.sourceLabel || '');
        setEmailType(data.emailType || '');
        setCategories(Array.isArray(data.categories) ? data.categories : []);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load email preferences.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query.token]);

  const anyOn = categories.some((category) => category.enabled);
  const invoiceSource = emailType === 'invoice-send' || emailType === 'invoice-reminder';

  const applyResult = (data, message) => {
    if (Array.isArray(data.categories)) setCategories(data.categories);
    if (data.companyName) setCompanyName(data.companyName);
    setNotice(message);
    setStatus('ready');
  };

  const post = async (body) => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch('/api/email-unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not update email preferences.');
      return data;
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeAll = async () => {
    if (!query.token) {
      await post({ action: 'unsubscribe-all', website: query.website });
      setNotice('You’re unsubscribed from product emails.');
      setStatus('done');
      return;
    }
    const data = await post({ token: query.token, action: 'unsubscribe-all' });
    applyResult(data, 'You’re unsubscribed from product emails.');
  };

  const sendMonthly = async () => {
    const data = await post({ token: query.token, action: 'monthly' });
    applyResult(data, 'We’ll send these once a month.');
  };

  const save = async () => {
    const selected = {};
    categories.forEach((category) => {
      selected[category.id] = category.enabled;
    });
    const data = await post({ token: query.token, action: 'save', categories: selected });
    applyResult(data, 'Preferences saved.');
  };

  const toggle = (id) => {
    setNotice('');
    setCategories((current) =>
      current.map((category) =>
        category.id === id ? { ...category, enabled: !category.enabled } : category
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-16">
      <div className="mx-auto max-w-lg">
        <p className="text-sm font-semibold tracking-tight text-gray-900">Upgraded</p>
        <div className="mt-6 rounded-xl border border-gray-100 bg-white p-6">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">Email preferences</h1>
          {companyName ? (
            <p className="mt-2 text-sm text-gray-600">For {companyName}.</p>
          ) : (
            <p className="mt-2 text-sm text-gray-600">
              Choose which product emails to keep. Invoices stay on.
            </p>
          )}

          {status === 'loading' && (
            <div className="mt-8 space-y-3">
              <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
              <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
              <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
            </div>
          )}

          {status === 'error' && (
            <p className="mt-6 text-sm text-red-600">{error}</p>
          )}

          {status === 'idle' && !query.website && (
            <p className="mt-6 text-sm text-gray-600">
              Open the unsubscribe link at the bottom of an Upgraded email. That link is tied to your company, so this page can update the right list.
            </p>
          )}

          {status === 'done' && (
            <p className="mt-6 text-sm font-medium text-gray-900">{notice}</p>
          )}

          {status === 'idle' && query.website && (
            <div className="mt-6">
              <p className="text-sm text-gray-600">
                Unsubscribe {query.website} from grant opportunities, setup reminders, weekly updates, and draft notifications.
              </p>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              <button
                type="button"
                onClick={() => unsubscribeAll().catch((err) => setError(err.message))}
                disabled={saving}
                className="mt-6 inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Unsubscribe'}
              </button>
            </div>
          )}

          {status === 'ready' && (
            <div className="mt-6">
              {sourceLabel && !invoiceSource && (
                <p className="text-sm text-gray-600">This link came from {sourceLabel}.</p>
              )}
              {invoiceSource && (
                <p className="text-sm text-gray-600">
                  This link was in an invoice email. Unsubscribing stops product updates. Invoices and payment reminders continue, because they cover amounts owed.
                </p>
              )}
              {notice && (
                <p className="mt-4 text-sm font-medium text-gray-900">{notice}</p>
              )}
              {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => unsubscribeAll().catch((err) => setError(err.message))}
                  disabled={saving || !anyOn}
                  className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Unsubscribe from all
                </button>
                <button
                  type="button"
                  onClick={() => sendMonthly().catch((err) => setError(err.message))}
                  disabled={saving}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Send monthly instead
                </button>
              </div>

              <ul className="mt-8 divide-y divide-gray-100">
                {categories.map((category) => (
                  <li key={category.id} className="flex items-start justify-between gap-4 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{category.label}</p>
                      <p className="mt-1 text-sm text-gray-600">{category.description}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={category.enabled}
                      aria-label={category.label}
                      onClick={() => toggle(category.id)}
                      className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
                        category.enabled ? 'bg-gray-900' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                          category.enabled ? 'left-5' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => save().catch((err) => setError(err.message))}
                disabled={saving}
                className="mt-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-40"
              >
                Save preferences
              </button>

              <p className="mt-6 text-sm text-gray-500">
                Invoices and payment reminders are separate. Reply to an invoice if a charge looks wrong.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
